import { Fragment, useState, useEffect } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import clsx from 'clsx'

const DEFAULT_POINTS = 100

const AdjustPointsDialog = ({
  data = false,
  inProgress = false,
  title = '',
  text = '',
  inProgressButtonText = 'Please wait...',
  setData = () => {},
  onConfirm = () => {},
}) => {
  const [value, setValue] = useState(`${DEFAULT_POINTS}`)
  const [total, setTotal] = useState(null)
  const [op, setOp] = useState('')

  useEffect(() => {
    if (!data) {
      setTotal(null)
      return
    }

    let newTotal = parseInt(data.points)

    if (op === 'add') {
      newTotal = newTotal + parseInt(value === '' ? '0' : value)
    } else if (op === 'sub') {
      newTotal = newTotal - parseInt(value === '' ? '0' : value)
    }
    setTotal(newTotal)
  }, [data, value, op])

  useEffect(() => {
    if (!data || !data.selectedOp) {
      return
    }

    setOp(data.selectedOp)
  }, [data])

  const closeModal = () => {
    setData(null)
  }

  const handleValueChange = (e) => {
    setValue(e.target.value)
  }

  return (
    <Transition appear show={data !== null} as={Fragment} className="group">
      <Dialog as="div" className="relative z-10" onClose={closeModal}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={clsx(
                  'group w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-sm transition-all',
                  inProgress && 'inProgress'
                )}
              >
                <Dialog.Title
                  as="h3"
                  className="mb-5 text-xl font-bold leading-none text-black-olive"
                >
                  {title}
                </Dialog.Title>
                <div className="mt-2">
                  <div
                    className="text-sm"
                    dangerouslySetInnerHTML={{ __html: text }}
                  />
                </div>

                {/** Adjust point form */}
                <div className="flex items-start">
                  <div>
                    <input
                      name="bonus"
                      type="number"
                      placeholder="100"
                      min="0"
                      value={value}
                      className={clsx(
                        'mb-4 mr-4 w-full rounded-[4px] border-light-gray py-1 text-xs placeholder-dark-gray sm:mb-0 sm:w-[250px]'
                      )}
                      onChange={handleValueChange}
                      onKeyDown={(event) => {
                        if (!/([0-9]|Backspace)/.test(event.key)) {
                          event.preventDefault()
                        }
                      }}
                    />
                    <div className="mt-2 text-center text-xs">
                      <div>
                        Current points total:{' '}
                        <strong>{data?.points ?? ''}</strong>
                      </div>
                      <div>
                        New points total: <strong>{total ?? ''}</strong>
                      </div>
                    </div>
                  </div>
                  <div className="flex">
                    <button
                      type="button"
                      className={clsx(
                        'rounded-[4px] rounded-r-none border-[1px] border-light-gray border-r-transparent px-4 py-2 text-xs leading-none transition-colors group-[.inProgress]:cursor-not-allowed group-[.inProgress]:opacity-50 group-[:not(.inProgress)]:hover:border-green group-[:not(.inProgress)]:hover:bg-green group-[:not(.inProgress)]:hover:text-white',
                        op === 'add' &&
                          'group-[:not(.inProgress)]:border-green group-[:not(.inProgress)]:bg-green group-[:not(.inProgress)]:text-white'
                      )}
                      disabled={inProgress}
                      onClick={() => setOp('add')}
                    >
                      ADD
                    </button>
                    <button
                      type="button"
                      className={clsx(
                        '-ml-[1px] rounded-[4px] rounded-l-none border-[1px] border-light-gray border-l-transparent px-4 py-2 text-xs leading-none transition-colors group-[.inProgress]:cursor-not-allowed group-[.inProgress]:opacity-50 group-[:not(.inProgress)]:hover:border-red-500 group-[:not(.inProgress)]:hover:bg-red-500 group-[:not(.inProgress)]:hover:text-white',
                        op === 'sub' &&
                          'group-[:not(.inProgress)]:border-red-500 group-[:not(.inProgress)]:bg-red-500 group-[:not(.inProgress)]:text-white'
                      )}
                      disabled={inProgress}
                      onClick={() => setOp('sub')}
                    >
                      SUB
                    </button>
                  </div>
                </div>

                <div className="mt-4 text-right">
                  <button
                    type="button"
                    className="rounded-full border-2 border-skin-button-accent px-6 py-2 text-xs leading-none transition-colors group-[.inProgress]:cursor-not-allowed group-[.inProgress]:opacity-50 group-[:not(.inProgress)]:hover:bg-skin-button-accent group-[:not(.inProgress)]:hover:text-white group-[:not(.inProgress)]:active:translate-y-[0.75px] group-[:not(.inProgress)]:active:scale-[0.99]"
                    disabled={inProgress}
                    onClick={closeModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="ml-2 inline-flex items-center rounded-full border-2 border-skin-button-accent bg-skin-button-accent px-6 py-2 text-xs leading-none text-white transition-colors group-[.inProgress]:cursor-not-allowed group-[.inProgress]:opacity-50 group-[:not(.inProgress)]:active:translate-y-[0.75px] group-[:not(.inProgress)]:active:scale-[0.99]"
                    disabled={inProgress}
                    onClick={() => onConfirm(data, value, op)}
                  >
                    {inProgress ? inProgressButtonText : 'Apply'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default AdjustPointsDialog
